/**
 * Вибір гілки для плаваючого чату сайту.
 *
 * Гілки в comments.app розводить data-page-id, і вони ті самі, що й у блоків
 * коментарів під плеєром: слуг аніме — на все аніме, `<слуг>-ep<N>` — на серію.
 * Тобто плаваючий чат не заводить окремих тредів, а відкриває вже наявні.
 *
 * Контекст сторінки перегляду кладе сюди плеєр (setChatContext), бо саме він
 * знає поточну серію. Вибір гілки живе в пам'яті: після перезавантаження
 * сторінки користувач обирає її знову.
 */

const SITE_PAGE_ID = 'site-chat'

export const useSiteChat = () => {
  // { slug, title, episodeIndex, episodeTitle } або null поза /watch/
  const context = useState('watchChatContext', () => null)
  // 'site' | 'anime' | 'episode' | null (ще не обрано)
  const scope = useState('siteChatScope', () => null)

  // Кнопка чату в плеєрі має відкривати панель кінотеатру, а не плаваюче
  // вікно: поруч із відео читати зручніше, ніж поверх нього. Плеєр слухає цей
  // лічильник — саме лічильник, бо просити відкрити можна не один раз
  const playerChatRequest = useState('siteChatPlayerRequest', () => 0)
  const requestPlayerChat = () => {
    playerChatRequest.value++
  }

  const setChatContext = (ctx) => {
    context.value = ctx
  }

  const clearChatContext = () => {
    context.value = null
  }

  // Доступні гілки залежать від сторінки: поза переглядом лишається тільки
  // загальна
  const scopes = computed(() => {
    const list = [
      {
        id: 'site',
        short: 'Сайт',
        label: 'Загальний чат сайту',
        hint: 'Спільна гілка для всіх сторінок'
      }
    ]

    const ctx = context.value
    if (!ctx?.slug) return list

    list.push({
      id: 'anime',
      short: 'Аніме',
      label: 'Чат аніме',
      hint: ctx.title || ctx.slug
    })

    if (ctx.episodeIndex !== null && ctx.episodeIndex !== undefined) {
      list.push({
        id: 'episode',
        short: 'Серія',
        label: 'Чат серії',
        hint: ctx.episodeTitle || `Серія ${ctx.episodeIndex + 1}`
      })
    }

    return list
  })

  // Обрана гілка може бути недоступна на поточній сторінці (пішли з /watch) —
  // тоді показуємо загальну, але сам вибір лишаємо на потім
  const activeScope = computed(() => {
    const id = scope.value
    if (id && scopes.value.some((s) => s.id === id)) return id
    return 'site'
  })

  const selectScope = (id) => {
    scope.value = id
  }

  // Чи є що обирати — на цьому тримається питання при першому відкритті
  const hasChoice = computed(() => scopes.value.length > 1)
  const isChosen = computed(() => !!scope.value)

  const chatConfig = computed(() => {
    const origin = import.meta.client ? window.location.origin : ''
    const ctx = context.value

    if (activeScope.value === 'anime' && ctx?.slug) {
      return {
        pageId: ctx.slug,
        pageTitle: ctx.title || ctx.slug,
        pageUrl: `${origin}/watch/${ctx.slug}`
      }
    }

    if (activeScope.value === 'episode' && ctx?.slug) {
      return {
        pageId: `${ctx.slug}-ep${ctx.episodeIndex + 1}`,
        pageTitle: `${ctx.title || ctx.slug} — ${
          ctx.episodeTitle || ctx.episodeIndex + 1
        }`,
        pageUrl: `${origin}/watch/${ctx.slug}?ep=${ctx.episodeIndex}`
      }
    }

    return {
      pageId: SITE_PAGE_ID,
      pageTitle: 'Загальний чат сайту',
      pageUrl: `${origin}/`
    }
  })

  return {
    context,
    scope,
    scopes,
    activeScope,
    hasChoice,
    isChosen,
    chatConfig,
    selectScope,
    setChatContext,
    clearChatContext,
    playerChatRequest,
    requestPlayerChat
  }
}
